<template>
  <div style="position: relative;">
    <div ref="spacer" :style="{width: '100%', height: formHeight+'px'}">
    </div>
    <div ref="form" class="card-team-management-form">
      <h2>Edit user</h2>
      <validation-observer v-slot="{ handleSubmit, reset }">
        <form @reset.prevent="onCancel(reset)"
              @submit.prevent="onSubmit(handleSubmit)">
          <div>
            <div>
              <div class="card-team-management-form-names">
                <field name="First name*"
                       v-model.trim="fields.firstname"
                       editable
                       rules="name|required|max:100" maxlength="100"
                />

                <field name="Last name*"
                       editable
                       v-model.trim="fields.lastname"
                       rules="name|required|max:100" maxlength="100"
                />
              </div>

              <field name="Login email*"
                     v-model.trim="fields.email"
                     :disabled="user && Object.prototype.hasOwnProperty.call(user, 'id')"
                     :tooltip="emailTooltip"
                     :position="'is-right'"
                     tooltip-classes="is-gray"
                     icon-pack="fa"
                     editable
                     rules="email|required|max:250" maxlength="250"
              />

              <validation-provider
                  v-slot="{ errors }"
                  v-model="phone"
                  name="phone"
                  :rules="{
                    required: false, phone_with_mask: true,
                  }">
                <b-field
                    :type="{ 'is-danger': errors.length }"
                    label="Contact number"
                    custom-class="personal-info__field-label"
                    class="personal-info__field field-input-phone">
                  <!-- todo: use ModernPhoneInput component -->
                  <b-input
                      name="contact number"
                      @focusin.native="focusIn"
                      @focusout.native="focusOut"
                      ref="phoneInput"
                      class="input-phone"
                      :style="cssVars"
                      placeholder="234-567-8910"
                      v-model="phone"
                      v-cleave="masks.phone"
                      >
                  </b-input>

                  <div class="input-phone-bottom">
                    <p class="help is-danger">{{errors + [ '&nbsp;' ]}}</p>
                    <small id=help-counter class="help counter is-invisible">
                      {{contactNumberLength}} / 10
                    </small>
                  </div>
                </b-field>
              </validation-provider>

              <b-field
                  v-if="isContractorUser || isSwitched"
                  custom-class="personal-info__field-label"
                  class="personal-info_is-admin-field"
                  :value="fields.isTeamUsersManagingAllowed"
              >
                <b-switch
                    v-model="fields.isTeamUsersManagingAllowed"
                    size="is-small"
                    class="enh-switch"
                    type="is-green"
                />
                <p>
                  Allow managing users and their settings (Admin)
                </p>
              </b-field>

              <b-field
                  label="Access*"
                  class="personal-info__field"
                  custom-class="personal-info__field-label"
                  :type="{ 'is-danger': accessErrors.length }"
                  :value="fields.access"
                  :message="accessErrors"
              >
                <b-select placeholder="Select a value" v-model="fields.access">
                  <option v-for="option in accessTypes" :value="option" :key="option">
                    {{ option }}
                  </option>
                </b-select>
              </b-field>

              <div
                  class="team-management-form-checkboxes-wrapper"
                  v-show="fields.access === accessLimited"
              >
                <b-field name="Select sections*">
                  <b-checkbox
                      v-for="item in fields.formResources"
                      :key="item.code"
                      v-model="item.checked"
                  >
                    <div class="team-management-form-checkbox-name">
                      {{ item.name }}
                    </div>

                    <div v-if="item.description" class="team-management-form-checkbox-description">
                      {{ item.description }}
                    </div>
                  </b-checkbox>
                </b-field>
              </div>


              <div class="buttons">
                <button class="button button-cancel" type="reset">
                  Cancel
                </button>
                <button class="button is-success" type="submit">Submit</button>
              </div>
            </div>
          </div>
        </form>
      </validation-observer>
    </div>
  </div>
</template>

<script>

import Field from '@/components/Field.vue';
import { teamUserCreate, teamUserUpdate } from '@/api/contractor';
import { mapGetters, mapMutations, mapState } from 'vuex';
import * as MUTATION from '@/store/mutations';

export default {
  name: 'CardTeamManagementForm',
  components: {
    Field,
  },
  props: {
    editSection: {
      type: String,
      default: '',
    },
    resources: {
      type: Array,
      default: () => [],
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    contractorId: {
      type: Number,
      default: null,
    },
  },
  data() {
    const accessFull = 'Full';
    const accessLimited = 'Limited';
    return {
      accessFull,
      accessLimited,
      accessTypes: [accessFull, accessLimited],
      fields: {
        firstname: null,
        lastname: null,
        email: null,
        isTeamUsersManagingAllowed: false,
        access: null,
        formResources: null,
      },
      contactNumberLength: 0,
      phone: '',
      formHeight: null,
      mutationObserver: null,
      accessErrors: [],
      submitIsTouched: false,
      countryCode: '+1',
      masks: {
        phone: {
          delimiters: ['-', '-', ''],
          blocks: [3, 3, 4],
          numericOnly: true,
          onValueChanged: (e) => {
            this.contactNumberLength = e.target.rawValue.length;
          },
        },
      },
    };
  },
  watch: {
    fields: {
      handler() {
        this.updateFormHeight();
        if (this.submitIsTouched) {
          this.validateAccess();
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState('user', { isContractorUser: (state) => state.isContractorUser }),
    ...mapGetters(['isSwitched']),
    emailTooltip() {
      const tooltipText = 'Set the email address your additional user will be using to login'
          + ' to Enhancify. The email address can only be associated with one Enhancify account'
          + ' at a time. When you add a user to your Enhancify account, we’ll send an email'
          + ' containing an authorization link to this email address.';
      return Object.prototype.hasOwnProperty.call(this.user, 'id') ? '' : tooltipText;
    },
    cssVars() {
      return {
        '--country-code': JSON.stringify(this.countryCode),
      };
    },
  },
  methods: {
    ...mapMutations({ preloader: MUTATION.SET_PRELOADER }),
    focusIn() {
      const helpCounter = document.getElementById('help-counter');
      helpCounter.classList.remove('is-invisible');
    },
    focusOut() {
      const helpCounter = document.getElementById('help-counter');
      helpCounter.classList.add('is-invisible');
    },
    onSubmit(handleSubmit) {
      this.submitIsTouched = true;
      this.validateAccess();
      handleSubmit(this.submit);
    },
    validateAccess() {
      let isValid = false;
      this.accessErrors = [];

      if (
        this.fields.access === this.accessLimited
        && this.fields.formResources.filter((i) => i.checked === true).length === 0
      ) {
        this.accessErrors = ['At least one item must be selected'];
      } else {
        isValid = true;
      }

      return isValid;
    },
    async submit() {
      const isValidAccess = this.validateAccess();
      if (!isValidAccess) {
        return;
      }

      this.preloader(true);
      const resourceCodes = [];
      if (this.fields.access === this.accessLimited) {
        this.fields.formResources.forEach((item) => {
          if (item.checked) {
            resourceCodes.push(item.code);
          }
        });
      }

      const payload = {
        firstname: this.fields.firstname,
        lastname: this.fields.lastname,
        contractorTeamData: {
          access: {
            type: this.fields.access,
            resourceCodes,
          },
          phone: '',
        },
      };

      if (this.isContractorUser || this.isSwitched) {
        payload.contractorTeamData.isTeamUsersManagingAllowed = this.fields
          .isTeamUsersManagingAllowed;
      }

      if (this.phone) {
        payload.contractorTeamData.phone = this.countryCode + this.phone.replaceAll(/\s|[-]/g, '');
      }
      try {
        if (Object.prototype.hasOwnProperty.call(this.user, 'id')) {
          await teamUserUpdate(this.contractorId, this.user.id, payload);
          this.$buefy.toast.open({ message: 'Update successful!', type: 'is-success', duration: 3000 });
        } else {
          payload.email = this.fields.email;
          await teamUserCreate(this.contractorId, payload);
          this.$buefy.toast.open({
            message: 'We sent the added user an email with an authorization link.',
            type: 'is-success',
            duration: 3000,
          });
        }
        this.$emit('after-submit');
      } catch (e) {
        if (e.response.data.error === 'email_already_used') {
          this.$buefy.snackbar.open({
            message: e.response.data.message,
            actionText: ' ',
            position: 'is-top',
            indefinite: true,
            type: 'is-danger',
          });
        } else {
          this.$buefy.snackbar.open({
            message: 'Something went wrong. Please try again or contact support.',
            actionText: ' ',
            position: 'is-top',
            indefinite: true,
            type: 'is-danger',
          });
        }
      }

      this.preloader(false);
    },
    onCancel() {
      this.$emit('cancel-form');
    },
    getFormResources() {
      const formResources = [];
      const userResourceCodes = Object.prototype.hasOwnProperty.call(this.user, 'id')
        ? this.user.access.resourceCodes
        : [];

      this.resources.forEach((resource) => {
        const formResource = JSON.parse(JSON.stringify(resource));
        formResources.push({
          ...formResource,
          checked: userResourceCodes.includes(resource.code),
        });
      });

      return JSON.parse(JSON.stringify(formResources));
    },
    updateFormHeight() {
      if (this.$refs.form) {
        this.$nextTick(() => {
          this.formHeight = this.$refs.form.offsetHeight;
        });
      }
    },
    getPhoneFromContractorTeamData(contractorTeamData) {
      if (contractorTeamData) {
        let countryCode = '+1';
        let phone = '';
        if (contractorTeamData?.phone) {
          const code = contractorTeamData.phone.match(/^\+\d/);
          phone = contractorTeamData.phone;
          if (code) {
            countryCode = code.join('');
            phone = contractorTeamData.phone.replace(/^\+\d/, '');
          }
        }
        return [countryCode, phone];
      }
      return null;
    },
  },
  mounted() {
    if (Object.prototype.hasOwnProperty.call(this.user, 'id')) {
      const user = JSON.parse(JSON.stringify(this.user));
      this.fields.firstname = user.firstname;
      this.fields.lastname = user.lastname;
      this.fields.email = user.email;
      [
        this.countryCode,
        this.phone,
      ] = this.getPhoneFromContractorTeamData(this.user.contractorTeamData);
      this.fields.formResources = this.getFormResources();
      this.fields.access = user.access.resourceCodes.length === 0
        ? this.accessFull
        : this.accessLimited;
    } else {
      this.fields.formResources = this.getFormResources();
      this.fields.access = this.accessLimited;
    }

    this.fields.isTeamUsersManagingAllowed = this.user.contractorTeamData
        ?.isTeamUsersManagingAllowed;

    this.updateFormHeight();
    window.addEventListener('resize', this.updateFormHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateFormHeight);
  },
};
</script>

<style lang="stylus" scoped>
.control.input-phone
  position relative
.control.input-phone:before
  position absolute
  content var(--country-code)
  top 13px
  left 12px
  z-index 20
.card-team-management-form >>> span.icon.is-right.has-text-danger
    height 100%
.field-input-phone
  >>>.input-phone-bottom
    display flex
    flex-wrap nowrap
    width 100%
    justify-content space-between
  position relative
  flex-wrap wrap
  >>>span.icon.is-right.has-text-danger
    height 100%
  .input-phone
    width 100%
    >>> input
      padding-left 33px
      height 48px
      border-radius 4px!important
      &::placeholder
        color #8a9499
.card-team-management-form
  border 1px solid #9db2bc
  border-radius 5px
  margin 15px 0
  padding 20px 15px
  position absolute
  top 0
  left -16px
  z-index 11
  width calc(100% + 32px)
  background-color white
  >>>.personal-info__field-label
    font-weight 400
    color #8A9499
  h2
    font-weight 400
    font-size 16px
    line-height 16px
    margin-bottom 15px
  >>> input,
  >>> select,
  >>> .select
    height 48px
    color #293033
    &[disabled]
      color #BDBDBD
      border solid 1px #8FA7B34D
      background-color #EDEDED
  >>> select
    width 100%
  >>> .select
    width 100%
    &::after
      border-color #363636 !important
    &:hover
      &::after
        border-color #3273dc !important
  .card-team-management-form-names
    display flex
    justify-content space-between
    @media screen and (max-width 430px)
      flex-direction column
    & > span
      width calc(50% - 15px)
      @media screen and (max-width 430px)
        width 100%
  .team-management-form-checkboxes-wrapper
    .field
      display flex
      flex-direction column
      .b-checkbox.checkbox
        padding 10px 0
        border-bottom solid 1px rgba(128,128,128,0.349)
        + .checkbox
          margin 0
        >>> input[type="checkbox"]
          + .check
            background-color transparent
            border 1px solid #8FA7B3
            box-shadow none
          &:checked
            + .check
              border 1px solid #17B548
              box-shadow none
              background #F5FCF5 url("data:image/svg+xml,%3Csvg fill='none' viewBox='0 0 12 9' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m11.824 0.17948c-0.2343-0.23931-0.6142-0.23931-0.8486 0l-7.1883 7.3413-2.7631-2.8218c-0.23431-0.23931-0.61418-0.23929-0.84853 0-0.23433 0.23929-0.23433 0.62724 0 0.86655l3.1874 3.2551c0.23424 0.23929 0.61439 0.23912 0.84853 0l7.6127-7.7745c0.2343-0.23929 0.2343-0.62726 0-0.86658z' clip-rule='evenodd' fill='%2317B548' fill-rule='evenodd'/%3E%3C/svg%3E") no-repeat center center
              background-size 70%
              transition none
            &:focus
              + .check
                box-shadow none
      .team-management-form-checkbox-description
        font-size 14px
        color #525F66
  .buttons
    display flex
    flex-direction row
    justify-content end
    margin-top 20px
    @media screen and (max-width 400px)
      flex-direction column
    .button
      width 135px
      height 48px
      border 1px solid #23D160
      font-size 19px
      text-transform uppercase
      @media screen and (max-width 400px)
        width 100%
        &:not(:last-child)
          margin-right 0
          margin-bottom 15px
      &.is-success
        border none
      &.button-cancel
        color #23D160
        &:hover
          background #17b548
          color white
.field.personal-info_is-admin-field
  display flex
  justify-items center
  align-items center
  margin-bottom 40px
  margin-top calc(40px - 22px)
  p
    padding-left 10px
    color: #8a9499
</style>
